import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Radio, Activity, CheckCircle2, Clock } from 'lucide-react';
import { RequireCapability } from '../../../components/auth/RequireCapability';
import { AdminConsoleLayout } from '../components/AdminConsoleLayout';
import { ModulePageHeader, ModuleLoadingState, ModuleCard } from '../../shared/components/ModulePageHeader';
import { EmptyState } from '../../shared/components/EmptyState';
import { ErrorState } from '../../shared/components/ErrorState';
import { useWorkspace } from '../../../contexts/WorkspaceContext';
import { supabase } from '../../../lib/supabaseClient';
import { getEventBusEntries } from '../../../services/platformService';
import type { EventBusEntry } from '../../../types/platformTypes';

const EventRow: React.FC<{ event: EventBusEntry }> = ({ event }) => (
  <div className="flex items-center gap-3 py-2 px-3 border-b border-[var(--border-default)] last:border-0 hover:bg-[var(--bg-elevated)] transition-colors">
    <div className={`w-2 h-2 rounded-full shrink-0 ${event.processed ? 'bg-emerald-500' : 'bg-amber-500'}`} />
    <code className="text-[11px] font-mono text-[var(--enterprise-primary,#2563eb)] w-40 shrink-0 truncate">
      {event.event_type}
    </code>
    <span className="text-[11px] text-[var(--text-tertiary)] w-16 shrink-0">{event.source}</span>
    <span className="text-[11px] text-[var(--text-secondary)] flex-1 truncate font-mono">
      {JSON.stringify(event.payload).slice(0, 80)}
    </span>
    <span className="text-[10px] text-[var(--text-tertiary)] w-32 shrink-0 text-right">
      {new Date(event.created_at).toLocaleString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit', day: '2-digit', month: '2-digit' })}
    </span>
  </div>
);

interface AdminEventBusPageProps {
  externalFilterType?: string;
}

export const AdminEventBusPage: React.FC<AdminEventBusPageProps> = ({ externalFilterType }) => {
  const { workspace } = useWorkspace();
  const orgId = workspace?.organizationId ?? '';
  const filterType = externalFilterType ?? '';

  // Resolve workspace_id from organization
  const wsQuery = useQuery({
    queryKey: ['org-workspace-id', orgId],
    queryFn: async () => {
      const { data } = await supabase
        .from('organizations')
        .select('workspace_id')
        .eq('id', orgId)
        .single();
      return data?.workspace_id as string | null;
    },
    enabled: !!orgId,
  });
  const workspaceId = wsQuery.data ?? '';

  const eventsQuery = useQuery({
    queryKey: ['admin-event-bus', workspaceId],
    queryFn: () => getEventBusEntries(workspaceId || undefined, 100),
    refetchInterval: 10000,
  });

  const events = eventsQuery.data || [];
  const filteredEvents = filterType
    ? events.filter((e) => e.event_type.includes(filterType))
    : events;
  const processedCount = events.filter((e) => e.processed).length;
  const uniqueTypes = [...new Set(events.map((e) => e.event_type))];

  return (
    <RequireCapability capability="admin.access" realRoleOnly>
      <AdminConsoleLayout>
        <div className="enterprise-page h-full overflow-y-auto">
          <ModulePageHeader icon={Radio} title="Event Bus" subtitle="Sistema de eventos interno em tempo real"
            rightContent={
              <span className="text-[11px] px-2.5 py-1 rounded-sm border border-amber-300 bg-amber-50 text-amber-700 dark:bg-amber-300 dark:border-amber-800 font-medium">
                Em breve
              </span>
            }
          />

          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 px-6 mb-6">
            <ModuleCard>
              <div className="text-center">
                <p className="text-2xl font-bold text-[var(--text-primary)]">{events.length}</p>
                <p className="text-xs text-[var(--text-secondary)]">Eventos (últimos)</p>
              </div>
            </ModuleCard>
            <ModuleCard>
              <div className="text-center">
                <p className="text-2xl font-bold text-emerald-500">{processedCount}</p>
                <p className="text-xs text-[var(--text-secondary)]">Processados</p>
              </div>
            </ModuleCard>
            <ModuleCard>
              <div className="text-center">
                <p className="text-2xl font-bold text-amber-500">{events.length - processedCount}</p>
                <p className="text-xs text-[var(--text-secondary)]">Pendentes</p>
              </div>
            </ModuleCard>
            <ModuleCard>
              <div className="text-center">
                <p className="text-2xl font-bold text-[var(--text-primary)]">{uniqueTypes.length}</p>
                <p className="text-xs text-[var(--text-secondary)]">Tipos Únicos</p>
              </div>
            </ModuleCard>
          </div>

          {/* Events List */}
          <div className="px-6 pb-8">
            {eventsQuery.isPending && <ModuleLoadingState label="Carregando eventos" />}

            {eventsQuery.isError && (
              <ErrorState message="Erro ao carregar event bus." onRetry={eventsQuery.refetch} />
            )}

            {!eventsQuery.isPending && !eventsQuery.isError && filteredEvents.length === 0 && (
              <EmptyState
                icon={Radio}
                title="Nenhum evento registrado"
                description="Os eventos serão exibidos aqui conforme o sistema emiti-los."
              />
            )}

            {filteredEvents.length > 0 && (
              <ModuleCard>
                <div className="divide-y divide-[var(--border-default)]">
                  {/* Header */}
                  <div className="flex items-center gap-3 py-2 px-3 text-[10px] font-medium text-[var(--text-tertiary)] uppercase tracking-wider">
                    <span className="w-2" />
                    <span className="w-40">Tipo</span>
                    <span className="w-16">Fonte</span>
                    <span className="flex-1">Payload</span>
                    <span className="w-32 text-right">Hora</span>
                  </div>
                  {filteredEvents.map((event) => (
                    <EventRow key={event.id} event={event} />
                  ))}
                </div>
              </ModuleCard>
            )}
          </div>
        </div>
      </AdminConsoleLayout>
    </RequireCapability>
  );
};
